import React from 'react';
import { Chip, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
} from '@barboxx/admin-components';
import { currencyFormatter, ID_WIDTH, useAuth } from '@barboxx/admin-utils';
import { OrderItem, OrderItemSubstitutions } from '@barboxx/models';
import { getMenuItemName } from '@barboxx/utils';

function getSubstitutionEntityName(
    sub: OrderItemSubstitutions
): string | undefined {
    if ('product' in sub && sub.product) {
        return sub.product.name;
    }
    if ('ingredient' in sub && sub.ingredient) {
        return sub.ingredient.name;
    }
    return undefined;
}

const BASE_COLUMNS: GridColDef<OrderItem.DetailsResponse>[] = [
    { field: 'id', headerName: 'Id', width: ID_WIDTH },

    {
        field: 'name',
        headerName: 'Name',
        valueGetter: (value, row) => {
            return getMenuItemName(row.menuItem);
        },
        width: 200,
    },
    {
        field: 'menuItem.type',
        headerName: 'Drink Type',
        width: 200,
        valueGetter: dataGridNestedObjectValueGetter,
    },
    {
        field: 'pourFormat.pourType.name',
        headerName: 'Pour Type',
        width: 100,
        valueGetter: dataGridNestedObjectValueGetter,
    },
    {
        field: 'unitPrice',
        headerName: 'Price',
        width: 100,
        valueFormatter: currencyFormatter,
        valueGetter: dataGridNestedObjectValueGetter,
    },
    {
        field: 'requestedQuantity',
        headerName: 'Requested Qty',
        width: 130,
    },
    {
        field: 'quantity',
        headerName: 'Fulfilled Qty',
        width: 130,
    },
    {
        field: 'subTotal',
        headerName: 'Sub Total',
        width: 100,
        valueFormatter: currencyFormatter,
        valueGetter: dataGridNestedObjectValueGetter,
    },
    {
        field: 'upCharge',
        headerName: 'Up Charge',
        width: 100,
        valueFormatter: currencyFormatter,
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 100,
    },
    {
        field: 'notes',
        headerName: 'Notes',
        width: 200,
    },
];

function buildSubstitutionsColumn(): GridColDef<OrderItem.DetailsResponse> {
    return {
        field: 'substitutions',
        headerName: 'Substitutions',
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }) => {
            const subs = row.substitutions;
            if (!subs?.length) {
                return null;
            }
            return (
                <Stack
                    direction="row"
                    gap={0.5}
                    flexWrap="wrap"
                    py={0.75}
                    alignItems="center"
                >
                    {subs.map(sub => {
                        const entityName =
                            getSubstitutionEntityName(sub) ?? String(sub.id);
                        return (
                            <Chip
                                key={sub.id}
                                label={entityName}
                                size="small"
                                variant="outlined"
                            />
                        );
                    })}
                </Stack>
            );
        },
    };
}

interface Props {
    orderItems: OrderItem.DetailsResponse[];
}

export const OrderItemsTable = ({ orderItems }: Props) => {
    const {
        auth: { user },
    } = useAuth();
    const isInternal = user.userType === 'Internal';

    const columns = React.useMemo(() => {
        const cols = BASE_COLUMNS.filter(c => {
            if (isInternal) {
                return true;
            } else {
                return !['upCharge'].includes(c.field);
            }
        });
        const substitutionsColumn = buildSubstitutionsColumn();
        const quantityColumnIndex = cols.findIndex(
            ({ field }) => field === 'quantity'
        );

        if (quantityColumnIndex >= 0) {
            cols.splice(quantityColumnIndex + 1, 0, substitutionsColumn);
        } else {
            cols.push(substitutionsColumn);
        }
        return cols;
    }, [isInternal]);

    return (
        <DataGridCard title="Order Items">
            <DataGrid
                columns={columns}
                rows={orderItems}
                paginationMode="client"
                sortingMode="client"
                rowHeight={56}
            />
        </DataGridCard>
    );
};
